import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ProveedorDeIdioma, useIdioma } from './idioma';
import { Iconos } from './iconos';
import { ProveedorDeSesion, useSesion } from './sesion';
import { ProveedorDeTema } from './tema';
import { Entrar } from './pantallas/entrar';
import { NuevaContrasena } from './pantallas/contrasena';
import { Registro } from './pantallas/registro';
import { Marcas } from './pantallas/marcas';
import { PantallaDeMarca } from './pantallas/marca';
import { Portada } from './pantallas/portada';
import { perezoso } from './perezoso';

/**
 * Cada pantalla se baja cuando se abre (06-10-2026: la web era un solo archivo de 3 MB, con el motor de video del
 * navegador incluido, que el celular bajaba y leía entero antes de enseñar el Inicio).
 */
const Perfil = perezoso(() => import('./pantallas/perfil'), 'Perfil');
const PagoListo = perezoso(() => import('./pantallas/pago'), 'PagoListo');
const PagoCancelado = perezoso(() => import('./pantallas/pago'), 'PagoCancelado');
const PasoEstrategia = perezoso(() => import('./pantallas/pasos/estrategia'), 'PasoEstrategia');
const PasoIdentidad = perezoso(() => import('./pantallas/pasos/identidad'), 'PasoIdentidad');
const Historial = perezoso(() => import('./pantallas/historial'), 'Historial');
const PasoDisenos = perezoso(() => import('./pantallas/pasos/disenos'), 'PasoDisenos');
const PasoMaterial = perezoso(() => import('./pantallas/pasos/material'), 'PasoMaterial');
const PasoResumenMarca = perezoso(() => import('./pantallas/pasos/resumen-marca'), 'PasoResumenMarca');
const PasoMes = perezoso(() => import('./pantallas/pasos/mes'), 'PasoMes');
const PasoNegocio = perezoso(() => import('./pantallas/pasos/negocio'), 'PasoNegocio');
const PasoPresentador = perezoso(() => import('./pantallas/pasos/presentador'), 'PasoPresentador');
const PasoEstudio = perezoso(() => import('./pantallas/pasos/estudio'), 'PasoEstudio');
const PasoPublicar = perezoso(() => import('./pantallas/pasos/publicar'), 'PasoPublicar');
const PasoMedicion = perezoso(() => import('./pantallas/pasos/medicion'), 'PasoMedicion');
const PasoRedes = perezoso(() => import('./pantallas/pasos/redes'), 'PasoRedes');
const PasoTextos = perezoso(() => import('./pantallas/pasos/textos'), 'PasoTextos');
const PasoVideos = perezoso(() => import('./pantallas/pasos/videos'), 'PasoVideos');
const PasoGrabado = perezoso(() => import('./pantallas/pasos/grabado'), 'PasoGrabado');
const PasoFichaVideo = perezoso(() => import('./pantallas/pasos/ficha-video'), 'PasoFichaVideo');
const Inicio = perezoso(() => import('./pantallas/pasos/inicio'), 'Inicio');
const PasoVideo = perezoso(() => import('./pantallas/pasos/video'), 'PasoVideo');
const PaginaCrear = perezoso(() => import('./pantallas/pasos/video'), 'PaginaCrear');
const PaginaElenco = perezoso(() => import('./pantallas/pasos/video'), 'PaginaElenco');
const PaginaStoryboards = perezoso(() => import('./pantallas/pasos/video'), 'PaginaStoryboards');

/** Lleva a la dirección nueva conservando lo que venía después de «?» (p. ej. ?prod= o ?reel=). */
function RedirigeConConsulta({ a }: { a: string }) {
  const { search } = useLocation();
  return <Navigate to={`../${a}${search}`} replace />;
}

/** Solo se entra con sesión; si no hay, al formulario de entrada. */
function ConSesion({ children }: { children: React.ReactNode }) {
  const { sesion, cargando } = useSesion();
  const { t } = useIdioma();
  if (cargando) return <main className="portada"><p className="apunte">{t('Un momento…')}</p></main>;
  return sesion ? <>{children}</> : <Navigate to="/entrar" replace />;
}

function Cargando() {
  const { t } = useIdioma();
  return <main className="portada"><p className="apunte">{t('Un momento…')}</p></main>;
}

export function App() {
  return (
    <ProveedorDeIdioma>
    <ProveedorDeTema>
    <ProveedorDeSesion>
      <Iconos />
      <BrowserRouter>
        <Suspense fallback={<Cargando />}>
        <Routes>
          <Route path="/" element={<Portada />} />
          <Route path="/entrar" element={<Entrar />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/nueva-contrasena" element={<NuevaContrasena />} />
          <Route path="/perfil" element={<ConSesion><Perfil /></ConSesion>} />
          <Route path="/pago/listo" element={<ConSesion><PagoListo /></ConSesion>} />
          <Route path="/pago/cancelado" element={<ConSesion><PagoCancelado /></ConSesion>} />
          <Route path="/marcas" element={<ConSesion><Marcas /></ConSesion>} />
          {/* La dirección de inicio de la app de AI Studio: los enlaces y marcadores viejos siguen funcionando. */}
          <Route path="/inicio" element={<Navigate to="/marcas" replace />} />
          <Route path="/marca/:id" element={<ConSesion><PantallaDeMarca /></ConSesion>}>
            <Route index element={<Navigate to="inicio" replace />} />
            <Route path="inicio" element={<Inicio />} />
            <Route path="negocio" element={<PasoNegocio />} />
            <Route path="estrategia" element={<PasoEstrategia />} />
            <Route path="redes" element={<PasoRedes />} />
            <Route path="identidad" element={<PasoIdentidad />} />
            {/* Producción → Video: la pantalla principal y sus puertas (29-09-2026). */}
            <Route path="video" element={<PasoVideo />} />
            <Route path="video/sala" element={<PasoPresentador />} />
            <Route path="video/elenco" element={<PaginaElenco />} />
            <Route path="video/storyboards" element={<PaginaStoryboards />} />
            <Route path="video/crear" element={<PaginaCrear />} />
            <Route path="video/estudio" element={<PasoEstudio />} />
            <Route path="video/reels" element={<PasoVideos />} />
            <Route path="video/grabado" element={<PasoGrabado />} />
            <Route path="video/pieza" element={<PasoFichaVideo />} />
            {/* Direcciones de antes: siguen funcionando. */}
            <Route path="presentador" element={<Navigate to="../video/sala" replace />} />
            <Route path="estudio" element={<RedirigeConConsulta a="video/estudio" />} />
            <Route path="videos" element={<RedirigeConConsulta a="video/reels" />} />
            <Route path="mes" element={<PasoMes />} />
            <Route path="textos" element={<PasoTextos />} />
            <Route path="disenos" element={<PasoDisenos />} />
            <Route path="material" element={<PasoMaterial />} />
            <Route path="marca" element={<PasoResumenMarca />} />
            <Route path="publicar" element={<PasoPublicar />} />
            <Route path="medicion" element={<PasoMedicion />} />
            <Route path="historial" element={<Historial />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </ProveedorDeSesion>
    </ProveedorDeTema>
    </ProveedorDeIdioma>
  );
}
